import React, { useState, useEffect } from 'react';
import '../css/studentProfile.css';
import {
  FaUser,
  FaIdCard,
  FaGraduationCap,
  FaPhone,
  FaCalendarAlt,
  FaBook,
  FaSchool,
  FaEdit,
  FaSave,
  FaTimes,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useApp } from '../../../../context/AppContext';

function StudentProfile() {
  const { user, roles, permissions } = useApp();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    studentid: 1001,
    b_form_no: '1234567890123',
    student_name: 'Ahmed Khan',
    father_name: 'Muhammad Khan',
    gender: 'M',
    dob: '2010-03-15',
    religion: 'Islam',
    class: '5',
    course_type: 'English',
    selected_books: ['Mathematics', 'Science', 'English'],
    semis_code: 'SCH001',
    school_name: 'Government Primary School, Karachi',
    disability_status: 'Not Disabled',
    student_contact: '03001234567',
    email: 'ahmed.khan@example.com',
    address: 'House #123, Street #5, Karachi, Pakistan',
    emergency_contact: '03009876543',
    emergency_contact_name: 'Muhammad Khan (Father)',
    enrollment_date: '2024-01-15',
    last_updated: '2024-01-20 10:30 AM',
  });

  const [editData, setEditData] = useState({});

  useEffect(() => {
    setEditData(profileData);
  }, [profileData]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData(profileData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData(profileData);
  };

  const handleSave = async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setProfileData(editData);
      setIsEditing(false);

      Swal.fire({
        title: 'Success',
        text: 'Profile updated successfully!',
        icon: 'success',
        confirmButtonColor: '#4CAF50',
      });
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to update profile. Please try again.',
        icon: 'error',
        confirmButtonColor: '#f44336',
      });
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const getGenderDisplay = gender => {
    return gender === 'M' ? 'Male' : 'Female';
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="eduss-student-profile">
      <div className="eduss-student-profile-header">
        <div className="eduss-student-profile-title">
          <h2>Student Profile</h2>
          <p>Manage your personal information and academic details</p>
          <p>Roles: {roles && roles.length > 0 ? roles.join(', ') : 'None'}</p>
          <p>Permissions: {permissions && permissions.length > 0 ? permissions.join(', ') : 'None'}</p>
        </div>
        <div className="eduss-student-profile-actions">
          {/* Only show Edit if user has EDIT_STUDENT permission */}
          {!isEditing && permissions.includes('EDIT_STUDENT') && (
            <button onClick={handleEdit} className="eduss-student-profile-btn edit">
              <FaEdit /> Edit Profile
            </button>
          )}
          {isEditing && (
            <div className="eduss-student-profile-edit-actions">
              <button onClick={handleCancel} className="eduss-student-profile-btn cancel">
                <FaTimes /> Cancel
              </button>
              <button onClick={handleSave} className="eduss-student-profile-btn save">
                <FaSave /> Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="eduss-student-profile-content">
        <div className="eduss-student-profile-section">
          <h3>
            <FaUser /> Basic Information
          </h3>
          <div className="eduss-student-profile-grid">
            <div className="eduss-student-profile-field">
              <label>Student ID</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="text"
                    name="studentid"
                    value={editData.studentid}
                    onChange={handleInputChange}
                    disabled
                  />
                ) : (
                  <span>{profileData.studentid}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>B-Form Number</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="text"
                    name="b_form_no"
                    value={editData.b_form_no}
                    onChange={handleInputChange}
                    maxLength="13"
                  />
                ) : (
                  <span>{profileData.b_form_no}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Student Name</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="text"
                    name="student_name"
                    value={editData.student_name}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{profileData.student_name}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Father&apos;s Name</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="text"
                    name="father_name"
                    value={editData.father_name}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{profileData.father_name}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Gender</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <select
                    name="gender"
                    value={editData.gender}
                    onChange={handleInputChange}
                  >
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                ) : (
                  <span>{getGenderDisplay(profileData.gender)}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Date of Birth</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="date"
                    name="dob"
                    value={editData.dob}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{formatDate(profileData.dob)}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Religion</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="text"
                    name="religion"
                    value={editData.religion}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{profileData.religion}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Contact Number</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="tel"
                    name="student_contact"
                    value={editData.student_contact}
                    onChange={handleInputChange}
                    placeholder="03XXXXXXXXX"
                  />
                ) : (
                  <span>{profileData.student_contact || 'Not provided'}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="eduss-student-profile-section">
          <h3>
            <FaGraduationCap /> Academic Information
          </h3>
          <div className="eduss-student-profile-grid">
            <div className="eduss-student-profile-field">
              <label>Class</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <select
                    name="class"
                    value={editData.class}
                    onChange={handleInputChange}
                  >
                    <option value="Kachi">Kachi</option>
                    <option value="1">Class 1</option>
                    <option value="2">Class 2</option>
                    <option value="3">Class 3</option>
                    <option value="4">Class 4</option>
                    <option value="5">Class 5</option>
                    <option value="6">Class 6</option>
                    <option value="7">Class 7</option>
                    <option value="8">Class 8</option>
                    <option value="9">Class 9</option>
                    <option value="10">Class 10</option>
                  </select>
                ) : (
                  <span>Class {profileData.class}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Course Type</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <select
                    name="course_type"
                    value={editData.course_type}
                    onChange={handleInputChange}
                  >
                    <option value="Sindhi">Sindhi</option>
                    <option value="Urdu">Urdu</option>
                    <option value="English">English</option>
                  </select>
                ) : (
                  <span>{profileData.course_type}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>SEMIS Code</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="text"
                    name="semis_code"
                    value={editData.semis_code}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{profileData.semis_code}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>School Name</label>
              <div className="eduss-student-profile-value">
                <span>{profileData.school_name}</span>
              </div>
            </div>

            <div className="eduss-student-profile-field full-width">
              <label>Selected Books</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <div className="eduss-student-profile-books-edit">
                    {profileData.selected_books.map(book => (
                      <label
                        key={book}
                        className="eduss-student-profile-checkbox"
                      >
                        <input
                          type="checkbox"
                          checked={editData.selected_books.includes(book)}
                          onChange={e => {
                            if (e.target.checked) {
                              setEditData(prev => ({
                                ...prev,
                                selected_books: [...prev.selected_books, book],
                              }));
                            } else {
                              setEditData(prev => ({
                                ...prev,
                                selected_books: prev.selected_books.filter(
                                  b => b !== book
                                ),
                              }));
                            }
                          }}
                        />
                        <span>{book}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="eduss-student-profile-books">
                    {profileData.selected_books.map(book => (
                      <span
                        key={book}
                        className="eduss-student-profile-book-tag"
                      >
                        {book}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="eduss-student-profile-section">
          <h3>
            <FaUser /> Additional Information
          </h3>
          <div className="eduss-student-profile-grid">
            <div className="eduss-student-profile-field">
              <label>Disability Status</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <select
                    name="disability_status"
                    value={editData.disability_status}
                    onChange={handleInputChange}
                  >
                    <option value="Not Disabled">Not Disabled</option>
                    <option value="Disabled">Disabled</option>
                  </select>
                ) : (
                  <span
                    className={`eduss-student-profile-status ${profileData.disability_status === 'Disabled' ? 'disabled' : 'active'}`}
                  >
                    {profileData.disability_status}
                  </span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Email</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={editData.email}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{profileData.email}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field full-width">
              <label>Address</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <textarea
                    name="address"
                    value={editData.address}
                    onChange={handleInputChange}
                    rows="3"
                  />
                ) : (
                  <span>{profileData.address}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Emergency Contact</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="tel"
                    name="emergency_contact"
                    value={editData.emergency_contact}
                    onChange={handleInputChange}
                    placeholder="03XXXXXXXXX"
                  />
                ) : (
                  <span>{profileData.emergency_contact}</span>
                )}
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Emergency Contact Name</label>
              <div className="eduss-student-profile-value">
                {isEditing ? (
                  <input
                    type="text"
                    name="emergency_contact_name"
                    value={editData.emergency_contact_name}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{profileData.emergency_contact_name}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="eduss-student-profile-section">
          <h3>
            <FaCalendarAlt /> System Information
          </h3>
          <div className="eduss-student-profile-grid">
            <div className="eduss-student-profile-field">
              <label>Enrollment Date</label>
              <div className="eduss-student-profile-value">
                <span>{formatDate(profileData.enrollment_date)}</span>
              </div>
            </div>

            <div className="eduss-student-profile-field">
              <label>Last Updated</label>
              <div className="eduss-student-profile-value">
                <span>{profileData.last_updated}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentProfile;
